import { logger } from '@app/logger';

import { type CommonCursorResponse } from '@common/utils/fetcher';

import { getLikes } from './LikesService';
import {
  getMatches,
  getMessages,
  type GetMatchesParams,
  type Match,
  type Message,
} from './MatchesService';
import { getTasks, type Task } from './TasksService';
import { type User } from './UsersService';

type MatchesStore = {
  getSortedMatches: () => Match[];
  setMatchesState: (state: 'loading' | 'loadingMore' | 'success') => void;
  setMatches: (data: {
    matches: Match[];
    pagination: CommonCursorResponse;
  }) => void;
};

type MessagesStore = {
  setMessagesForMatch: (matchId: string, messages: Message[]) => void;
};

export async function refreshLikesStore(setLikes: (likes: User[]) => void) {
  try {
    const likes = await getLikes();
    if (Array.isArray(likes)) {
      setLikes(likes);
    }
  } catch (error) {
    logger.captureException(error, {
      tags: { section: 'Refresh likes store error' },
      extra: { error },
    });
  }
}

/**
 * Told how a refresh went, before the store leaves its loading state: an
 * infinite-scroll loader that backs off after a failure must know it before
 * its sentinel's observer is turned back on.
 */
type RefreshCallbacks = {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
};

export async function refreshMatchesStore(
  store: MatchesStore,
  params?: GetMatchesParams,
  callbacks?: RefreshCallbacks,
) {
  try {
    const alreadyLoadedMatches = store.getSortedMatches() ?? [];
    const limit = alreadyLoadedMatches.length || 10;

    store.setMatchesState(
      alreadyLoadedMatches.length ? 'loadingMore' : 'loading',
    );

    const response = await getMatches({ limit, ...params });

    if (response.matches) {
      store.setMatches({
        ...response,
        matches: [...alreadyLoadedMatches, ...response.matches],
      });
    }

    callbacks?.onSuccess?.();
  } catch (error) {
    logger.captureException(error, {
      tags: { section: 'Refresh matches store error' },
      extra: { error },
    });
    callbacks?.onError?.(error);
  } finally {
    store.setMatchesState('success');
  }
}

export async function refreshTasksStore(setTasks: (tasks: Task[]) => void) {
  try {
    const response = await getTasks();
    setTasks(response.tasks);
  } catch (error) {
    logger.captureException(error, {
      tags: { section: 'Refresh tasks store error' },
      extra: { error },
    });
  }
}

export async function refreshMessagesStore({
  store,
  matchId,
}: {
  store: MessagesStore;
  matchId: string;
}) {
  try {
    const response = await getMessages({
      matchId,
    });

    store.setMessagesForMatch(matchId, response.messages);
  } catch (error) {
    logger.captureException(error, {
      tags: { section: 'Refresh messages store error' },
      extra: { error },
    });
  }
}
